'use client'

import { captureException } from '@sentry/nextjs'
import { useEffect } from 'react'

const SERWIST_SW_PATH = '/serwist/sw.js'
const SERVICE_WORKER_UPDATE_INTERVAL_MS = 30 * 60 * 1000

function captureServiceWorkerError(error: unknown, endpoint: string): void {
  captureException(error, {
    tags: {
      area: 'service-worker',
      endpoint,
    },
  })
}

async function updateServiceWorker(registration: ServiceWorkerRegistration): Promise<void> {
  if (!(registration.active || registration.waiting)) {
    return
  }

  try {
    await registration.update()
  } catch (error) {
    captureServiceWorkerError(error, 'update-check')
  }
}

export function ServiceWorkerProvider() {
  useEffect(() => {
    let disposed = false
    let updateIntervalId: number | undefined

    if ('serviceWorker' in navigator) {
      const isLocalhost =
        window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      const shouldDisableServiceWorker = process.env.NODE_ENV === 'development' || isLocalhost

      if (shouldDisableServiceWorker) {
        navigator.serviceWorker
          .getRegistrations()
          .then(async (registrations) => {
            const serwistRegistrations = registrations.filter((registration) => {
              const scriptUrl =
                registration.active?.scriptURL ??
                registration.installing?.scriptURL ??
                registration.waiting?.scriptURL ??
                ''

              return scriptUrl.includes(SERWIST_SW_PATH)
            })

            if (!serwistRegistrations.length) {
              return
            }

            await Promise.all(serwistRegistrations.map((registration) => registration.unregister()))

            if (
              navigator.serviceWorker.controller &&
              !window.sessionStorage.getItem('trellis-dev-sw-cleared')
            ) {
              window.sessionStorage.setItem('trellis-dev-sw-cleared', 'true')
              window.location.reload()
            }
          })
          .catch((err) => console.error('SW cleanup failed:', err))
      } else {
        navigator.serviceWorker
          .register(SERWIST_SW_PATH, { scope: '/' })
          .then((registration) => {
            // Browser automation and privacy tools can block registration without rejecting.
            if (disposed || !registration) {
              return
            }

            const checkForUpdate = () => updateServiceWorker(registration)
            updateIntervalId = window.setInterval(checkForUpdate, SERVICE_WORKER_UPDATE_INTERVAL_MS)

            checkForUpdate()
          })
          .catch((error) => captureServiceWorkerError(error, 'registration'))
      }
    }

    return () => {
      disposed = true
      if (updateIntervalId !== undefined) {
        window.clearInterval(updateIntervalId)
      }
    }
  }, [])

  return null
}
